import { useTranslation } from '@/i18n';
import { Badge } from '@/components/Shared/Badge';
import type { FacilityFlowCoreModulesFleetDtosVehicleCheckDto as VehicleCheckDto } from '@/api/generated/models';
import { isoDate } from '@/utils/dateUtils';
import {
  VEHICLE_CHECK_KINDS,
  currentCheckOf,
  formatValidUntil,
  vehicleCheckStatus,
  type VehicleCheckStatus,
} from '@/utils/vehicleChecks';

const STATUS_VARIANT: Record<VehicleCheckStatus, 'success' | 'warning' | 'danger' | 'default'> = {
  ok: 'success',
  dueSoon: 'warning',
  expired: 'danger',
  missing: 'default',
};

interface VehicleCheckBadgesProps {
  /** Checks of the vehicle, newest first — the first of each kind counts. */
  checks: VehicleCheckDto[] | null | undefined;
}

/** First aid kit, inspection and vignette of a vehicle at a glance: ok, due soon, expired or missing (FAFD-69). */
export default function VehicleCheckBadges({ checks }: VehicleCheckBadgesProps) {
  const { t } = useTranslation('vehicles');
  const today = isoDate(new Date());

  return (
    <div className="flex flex-wrap gap-1">
      {VEHICLE_CHECK_KINDS.map((kind) => {
        const check = currentCheckOf(checks, kind);
        const status = vehicleCheckStatus(check?.validUntil, today);
        const label = t(`vehicles:checks.kinds.${kind}`);
        return (
          <Badge
            key={kind}
            variant={STATUS_VARIANT[status]}
            title={
              check
                ? t('vehicles:checks.validUntil', { date: formatValidUntil(kind, check.validUntil) })
                : t('vehicles:checks.notRecorded')
            }
          >
            {t(`vehicles:checks.badge.${status}`, { kind: label })}
          </Badge>
        );
      })}
    </div>
  );
}
